// Shared helpers for reading session source-summary fields and formatting counts.
import type { AppSession, SourcePreflightResponse } from './api'

export function readNumber(value: number | undefined | null): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

export function sessionChangedFiles(session: AppSession): number {
  return readNumber(session.source_summary?.changedFileCount ?? null)
}

export function sessionHasUnsavedChanges(session: AppSession): boolean {
  return Boolean(session.source_summary?.hasUnsavedChanges ?? false)
}

export function sessionSavedChanges(session: AppSession): number {
  return readNumber(session.source_summary?.savedChangeCount ?? null)
}

export function sessionChangesApplied(session: AppSession): boolean {
  return Boolean(session.source_summary?.broughtBack ?? true)
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}

export function formatPreflightDescription(
  response: SourcePreflightResponse,
  confirmLabel: string,
): string {
  const warnings = response.warnings
    .map((warning) => `- ${warning.message}`)
    .join('\n')
  return [
    response.summary,
    warnings ? `\nPlease note:\n${warnings}` : '',
    '',
    confirmLabel,
  ].filter(Boolean).join('\n')
}

/**
 * Run a destructive-action preflight and turn it into confirm-dialog text.
 * A failed safety check degrades to the fallback summary — the action is
 * still confirmable, just without the per-session change details.
 */
export async function confirmPreflightAction(
  confirmLabel: string,
  loadPreflight: () => Promise<SourcePreflightResponse>,
  fallbackSummary: string,
): Promise<string> {
  try {
    const response = await loadPreflight()
    return formatPreflightDescription(response, confirmLabel)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'The safety check could not run.'
    return [
      fallbackSummary,
      '',
      `Safety check unavailable: ${message}`,
      '',
      confirmLabel,
    ].join('\n')
  }
}
